import { CONTACTS, PRICING_FACTS, SITE_NAME, SITE_URL } from "./site-facts";

export { CONTACTS, PRICING_FACTS } from "./site-facts";

export const ORGANIZATION_SCHEMA: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  alternateName: ["Полотно", "Polotno Transcribe", "Полотно транскрибация"],
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/favicon-512x512.png`,
  description: "Сервис автоматической расшифровки аудио и видео в текст с помощью искусственного интеллекта.",
  // В выдаче по слову «polotno» стоит чужой SDK (polotno.com) — явно разводим сущности
  disambiguatingDescription:
    "Сервис расшифровки аудио и видео в текст на polotno.tech. Не связан с Polotno SDK (polotno.com) — библиотекой canvas-редактора для дизайна.",
  // Коммерческие факторы Яндекса: доступность контактов и сведений об исполнителе
  legalName: CONTACTS.legalName,
  taxID: CONTACTS.taxId,
  email: CONTACTS.email,
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer support",
    email: CONTACTS.email,
    url: `${SITE_URL}/contacts`,
    availableLanguage: ["Russian"]
  },
  sameAs: [
    CONTACTS.transcribeBotUrl,
    CONTACTS.supportBotUrl,
    CONTACTS.yandexBusinessUrl,
    CONTACTS.productHuntUrl,
    CONTACTS.indieHackersUrl
  ]
};

/**
 * Автор сервиса и статей блога. Ссылается на организацию, живёт по постоянному
 * @id: страницы блога подставляют его в `author`, страница /about — раскрывает целиком.
 */
export const PERSON_SCHEMA: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE_URL}/about#person`,
  name: CONTACTS.legalName,
  // Ведём на /contacts: имя и реквизиты показаны там, на /about их намеренно нет
  url: `${SITE_URL}/contacts`,
  jobTitle: "Разработчик и основатель Polotno",
  worksFor: { "@id": `${SITE_URL}/#organization` },
  knowsAbout: [
    "автоматическая транскрибация речи",
    "распознавание речи",
    "обработка аудио и видео",
    "разработка веб-сервисов"
  ]
};

export const WEBSITE_SCHEMA: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  alternateName: "Полотно",
  url: `${SITE_URL}/`,
  inLanguage: "ru-RU",
  publisher: { "@id": `${SITE_URL}/#organization` }
};

/**
 * Описание сервиса как приложения. Offer'ы отражают действующие тарифы,
 * блок aggregateRating намеренно отсутствует — без реальных отзывов на странице
 * он нарушает правила Google для разметки отзывов.
 */
export const softwareApplicationSchema = (): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": `${SITE_URL}/#software`,
  name: SITE_NAME,
  alternateName: "Полотно",
  url: `${SITE_URL}/`,
  description:
    "Онлайн-сервис транскрибации: расшифровка аудио и видео в текст, разделение по спикерам, таймкоды и AI-саммари.",
  applicationCategory: "MultimediaApplication",
  operatingSystem: "Web Browser, Telegram",
  inLanguage: "ru-RU",
  publisher: { "@id": `${SITE_URL}/#organization` },
  offers: [
    {
      "@type": "Offer",
      name: `Пробный период — ${PRICING_FACTS.trial.minutes} минут`,
      price: String(PRICING_FACTS.trial.price),
      priceCurrency: "RUB",
      url: `${SITE_URL}/pricing`
    },
    {
      "@type": "Offer",
      name: `Basic — ${PRICING_FACTS.basic.minutes} минут в месяц`,
      price: String(PRICING_FACTS.basic.price),
      priceCurrency: "RUB",
      url: `${SITE_URL}/pricing`
    },
    {
      "@type": "Offer",
      name: `Pro — ${PRICING_FACTS.pro.minutes} минут в месяц`,
      price: String(PRICING_FACTS.pro.price),
      priceCurrency: "RUB",
      url: `${SITE_URL}/pricing`
    },
    {
      "@type": "Offer",
      name: `Basic — ${PRICING_FACTS.basic.minutes} минут в месяц, оплата за год`,
      price: String(PRICING_FACTS.basic.yearlyPrice),
      priceCurrency: "RUB",
      url: `${SITE_URL}/pricing`,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: String(PRICING_FACTS.basic.yearlyPrice),
        priceCurrency: "RUB",
        billingDuration: "P1Y"
      }
    },
    {
      "@type": "Offer",
      name: `Pro — ${PRICING_FACTS.pro.minutes} минут в месяц, оплата за год`,
      price: String(PRICING_FACTS.pro.yearlyPrice),
      priceCurrency: "RUB",
      url: `${SITE_URL}/pricing`,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: String(PRICING_FACTS.pro.yearlyPrice),
        priceCurrency: "RUB",
        billingDuration: "P1Y"
      }
    }
  ],
  featureList: [
    "Расшифровка аудио в текст",
    "Преобразование видео в текст",
    "Транскрибация ссылок YouTube, VK Video и Rutube",
    "Разделение по спикерам (диаризация)",
    "Таймкоды и встроенный редактор",
    "AI-саммари и ключевые тезисы",
    "Telegram-бот для транскрибации",
    "Экспорт в TXT, DOCX, PDF и SRT",
    "Поддержка 99 языков"
  ]
});

/** FAQ главной страницы — отображается в FaqSection и дублируется в разметке FAQPage */
export const HOME_FAQ = [
  {
    question: "Как расшифровать аудио или видео в текст?",
    answer:
      "Загрузите файл на Polotno или вставьте ссылку на YouTube, VK Video либо Rutube. Нейросеть автоматически распознает речь, расставит знаки препинания и вернёт готовый текст с таймкодами."
  },
  {
    question: "Какие форматы файлов поддерживаются?",
    answer: `MP3, M4A, WAV, OGG, FLAC, MP4, MOV, AVI, MKV и другие популярные форматы. Максимальный размер файла — ${PRICING_FACTS.maxFileSizeGb} ГБ, длительность — до ${PRICING_FACTS.maxDurationHours} часов.`
  },
  {
    question: "Сколько стоит транскрибация?",
    answer: `Начать можно с пробного периода: ${PRICING_FACTS.trial.minutes} минут за ${PRICING_FACTS.trial.price} ₽ на ${PRICING_FACTS.trial.days} дней. Дальше доступны подписки Basic (${PRICING_FACTS.basic.minutes} минут за ${PRICING_FACTS.basic.price} ₽ в месяц) и Pro (${PRICING_FACTS.pro.minutes} минут за ${PRICING_FACTS.pro.price} ₽ в месяц), а также пакеты на ${PRICING_FACTS.packages.slice(0, -1).join(", ")} или ${PRICING_FACTS.packages[PRICING_FACTS.packages.length - 1]} минут. При оплате за год — ${PRICING_FACTS.basic.yearlyPrice} ₽ и ${PRICING_FACTS.pro.yearlyPrice} ₽ (скидка ${PRICING_FACTS.yearlyDiscountPercent}%).`
  },
  {
    question: "Насколько точная получается расшифровка?",
    answer:
      "Для записей хорошего качества точность достигает 95–99 %. На результат влияют чёткость речи, уровень фонового шума и качество микрофона."
  },
  {
    question: "Как быстро обрабатывается запись?",
    answer:
      "В среднем обработка занимает 10–15 % от длительности записи: часовой файл будет расшифрован примерно за 5–7 минут."
  },
  {
    question: "Можно ли работать через Telegram?",
    answer:
      "Да, у сервиса есть Telegram-бот: отправьте ему голосовое, аудио, видео или ссылку. Аккаунт можно связать с веб-версией, тогда минуты и история будут общими."
  },
  {
    question: "Полотно и Polotno Studio — это одно и то же?",
    answer:
      "Нет. Полотно — сервис расшифровки аудио и видео в текст, он находится на polotno.tech. Polotno SDK на polotno.com — другой продукт, библиотека canvas-редактора для дизайна. Мы никак не связаны."
  },
  {
    question: "В каких форматах можно скачать результат?",
    answer:
      "Готовую расшифровку можно выгрузить в TXT, DOCX, PDF или SRT — последний подходит для субтитров на YouTube и в соцсетях."
  }
];
